// @ts-nocheck
/* eslint-enable */

import type { ReactElement } from 'react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { iconForListing } from 'app/client/utils/map/gmapIcons';
import { useSelector } from 'react-redux';
import controllerMarkers from './controller';
import MapMarker from 'app/shared/modules/map/MapMarker';
import mapMarkerController from 'app/shared/modules/map/MapMarker/controller';
import { MapPopupGlobalStyle } from 'app/shared/modules/map/MapMarker/PopupStyles';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { IconType, UserItemType } from 'app/types/map.type';

interface Marker {
  listing: ListingDetails;
  setIcon: (icon: unknown) => void;
  infoWindow: {
    open: (map: unknown, marker: Marker) => void;
    close: () => void;
    setZIndex: (z: number) => void;
  };
  setOptions: (options: unknown) => void;
}

interface Props {
  activeMarkerMaloneLotId: string;
  currentListing: ListingDetails;
  isMobile: boolean;
  listings: Array<ListingDetails>;
  map: object | null | undefined;
  onListingMarkerMouseOut: () => void;
  onListingMarkerMouseOver: () => void;
  onMarkerClick: () => void;
  onMarkerStoreReady?: (updateFn: (maloneLotIdEncoded: string, updatedListing: ListingDetails) => void) => void;
}

// Helper function to convert ListingDetails to the format expected by iconForListing
const getListingForIcon = (listing: ListingDetails) => ({
  iconType: (listing.iconType as IconType) || 'building',
  listingType: listing.listingType as 'room' | undefined,
  userItemTypes: listing.userItemTypes as Array<UserItemType>,
});

const ListingMarkers: React.FC<Props> = (props: Props) => {
  const {
    activeMarkerMaloneLotId,
    currentListing,
    onListingMarkerMouseOut,
    onListingMarkerMouseOver,
    onMarkerClick,
    onMarkerStoreReady,
    map,
    listings,
  } = props;

  const googleMapsKey = useSelector((state) => state.app.googleMaps?.key);
  const [listingMapMarkers, updateListingMapMarkers] = useState<Array<ReactElement>>([]);
  // Use a ref instead of state for marker store to ensure it persists across renders
  const markerStoreRef = useRef<Record<string, unknown>>({});

  // Store references to markers displayed on map for later quick lookup,
  // as opposed to re-rendering the entire array of markers in order to
  // find the one that we want to set as an active marker.
  const storeMarkerRef = (marker: Marker, listing: ListingDetails): void => {
    marker.listing = listing; // Needed for later reference in determining active icon...
    markerStoreRef.current[listing.maloneLotIdEncoded] = marker;
  };

  // Function to update a specific marker's icon
  // This is needed because Google Maps markers are not React components and don't automatically
  // re-render when Redux state changes. When a listing's userItemTypes (viewed, favorite, hidden)
  // are updated in Redux, we need to manually update the corresponding Google Maps marker instance
  // by calling its setIcon() method with the new icon that reflects the updated state.
  // Without this, markers would only update on page reload or when React re-creates them entirely.
  const updateMarkerIcon = (maloneLotIdEncoded: string, updatedListing: ListingDetails): void => {
    const marker = markerStoreRef.current[maloneLotIdEncoded] as Marker;
    if (marker) {
      // Update the listing data stored with the marker
      marker.listing = updatedListing;
      // Generate and set the new icon
      const newIcon = iconForListing(getListingForIcon(updatedListing), false);
      marker.setIcon(newIcon);
    }
  };

  // Notify parent when the updateMarkerIcon function is ready
  useEffect(() => {
    if (onMarkerStoreReady) {
      onMarkerStoreReady(updateMarkerIcon);
    }
  }, [onMarkerStoreReady]);

  // Update existing markers when listings data changes (e.g., favorites toggled)
  // This useEffect watches for changes in the Redux listings array and synchronizes
  // the Google Maps marker instances with the updated data. When userItemTypes change
  // (like when a user favorites/unfavorites a listing), Redux updates the listings array,
  // but the Google Maps markers still hold references to the old listing objects.
  // This effect ensures markers always reflect the current Redux state.
  useEffect(() => {
    listings.forEach((listing) => {
      const marker = markerStoreRef.current[listing.maloneLotIdEncoded] as Marker;
      if (marker && marker.listing) {
        // Check if userItemTypes has changed
        const oldUserItemTypes = marker.listing.userItemTypes || [];
        const newUserItemTypes = listing.userItemTypes || [];

        if (JSON.stringify(oldUserItemTypes.sort()) !== JSON.stringify(newUserItemTypes.sort())) {
          marker.listing = listing;
          const newIcon = iconForListing(getListingForIcon(listing), false);
          marker.setIcon(newIcon);
        }
      }
    });
  }, [listings]);

  // Generate array of listing markers displayed on map and only
  // update components when listings have actually changed.
  useEffect(() => {
    const listingMarkerComponentArray: Array<ReactElement> = [];

    for (let i = 0, total = listings.length; i < total; i++) {
      const listing = listings[i];

      listingMarkerComponentArray.push(
        <MapMarker
          dataObj={listing}
          icon={iconForListing(getListingForIcon(listing))}
          infoWindowContent={controllerMarkers.listingInfoWindowContent(listing, googleMapsKey)}
          key={`listing-${listing.maloneLotIdEncoded}`}
          lat={listing.geo.lat}
          lon={listing.geo.lon}
          map={map}
          onClick={onMarkerClick}
          onMouseOut={onListingMarkerMouseOut}
          onMouseOver={onListingMarkerMouseOver}
          storeMarkerRef={storeMarkerRef}
          zIndex={2}
        />,
      );
    }

    updateListingMapMarkers(listingMarkerComponentArray);
    // eslint-disable-next-line
  }, [currentListing, listings]);

  // Handle toggle of active marker without causing entire listing array to rerender.
  useEffect(() => {
    // If activeMarkerMaloneLotId is null, close any open info windows
    if (!activeMarkerMaloneLotId) {
      // Use the controller's utility method to force close all info windows
      mapMarkerController.closeAllInfoWindows();

      // Also reset all markers to their normal state
      Object.values(markerStoreRef.current).forEach((markerObj) => {
        const marker = markerObj as Marker;
        if (marker && marker.infoWindow) {
          mapMarkerController.handleShowInfoWindow(marker, false);
          const icon = iconForListing(getListingForIcon(marker.listing), false);
          marker.setIcon(icon);
          marker.setOptions({ zIndex: 2 });
        }
      });
      return;
    }

    const marker = markerStoreRef.current[activeMarkerMaloneLotId] as Marker;
    if (!marker || !map) {
      return;
    }

    let icon = iconForListing(getListingForIcon(marker.listing), true);
    marker.setIcon(icon);
    // Let's use the mapMarkerController to open the info window consistently
    mapMarkerController.handleShowInfoWindow(marker, true, true);
    marker.infoWindow.setZIndex(999);
    marker.setOptions({ zIndex: 999 });

    return () => {
      icon = iconForListing(getListingForIcon(marker.listing), false);
      // Use the controller to close the info window properly
      mapMarkerController.handleShowInfoWindow(marker, false);
      marker.setIcon(icon);
      marker.setOptions({ zIndex: 998 });
    };

    // eslint-disable-next-line
  }, [activeMarkerMaloneLotId]);

  return (
    <>
      <MapPopupGlobalStyle />
      {listingMapMarkers}
    </>
  );
};

export default ListingMarkers;
